import { useState, useEffect } from 'react';

import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from 'next-themes';
import SEO from '../components/SEO';
import IntelligenceTicker from '../components/IntelligenceTicker';
import HeroDashboardDemo from '../components/HeroDashboardDemo';
import {
  Activity, BrainCircuit, Send, Video, ArrowRight, ChevronRight,
  HeartPulse, Building2, Scale, Utensils, Hotel, BarChart2, Store,
  MessageCircle, Zap, Globe, Shield, Bot, Check
} from 'lucide-react';
import LocaleLink from '../components/LocaleLink';

const HERO_DARK = '/media/hero-dark.png';
const HERO_LIGHT = '/media/hero-light.png';
const AI_VIZ = '/media/ai-viz.png';

const content = {
  ar: {
    hero: {
      badge: 'عربي أولاً · متعدد اللغات · مدعوم بالذكاء الاصطناعي',
      h1a: 'بنية تحتية ذكية',
      h1b: 'متعددة اللغات',
      h1c: 'للذكاء والنشر الآلي',
      sub: 'أنظمة ذكاء اصطناعي عربية الجذور، مصممة للجيل القادم من الأعمال الذكية.',
      cta1: 'ابدأ مجاناً',
      cta2: 'احجز عرضاً تجريبياً',
    },
    products: {
      label: 'منظومتنا',
      h2: 'أربعة أنظمة ذكية. منصة واحدة.',
      sub: 'بنية تحتية متكاملة للذكاء الاصطناعي والنشر الآلي، مصممة للأعمال الرقمية متعددة اللغات.',
      items: [
        { icon: Activity, color: 'indigo', tag: 'ذكاء', name: 'QuidPulse', desc: 'خلاصة ذكاء متعددة اللغات للعملات المشفرة بالعربية والإنجليزية. إشارات وتحليلات مدعومة بالذكاء الاصطناعي.' },
        { icon: BrainCircuit, color: 'purple', tag: 'تحليل', name: 'Quidanalyzer', desc: 'مساعد ذكاء اصطناعي محادثي لتحليل العملات المشفرة بعمق ورؤى فورية متعددة اللغات.' },
        { icon: Send, color: 'amber', tag: 'نشر', name: 'Post Publisher', desc: 'نظام إنشاء ونشر محتوى آلي مدعوم بالذكاء الاصطناعي. أنشئ وجدوِل ووزّع المحتوى متعدد اللغات.' },
        { icon: Video, color: 'emerald', tag: 'فيديو', name: 'Video Publisher', desc: 'خط إنتاج آلي لإنشاء الفيديو متعدد اللغات والتعليق الصوتي والتوزيع، مدعوم بالذكاء الاصطناعي.' },
      ],
    },
    stats: [
      { value: '٢+', label: 'لغة مدعومة' },
      { value: '٤', label: 'منتجات' },
      { value: '١٠+', label: 'صناعات مخططة' },
      { value: '٢٤/٧', label: 'نشر آلي' },
    ],
    features: {
      label: 'إمكانيات المنصة',
      h2: 'مبني للذكاء على نطاق واسع',
      sub: 'كل مكوّن في كويدوري مصمم للعمل بذكاء، على نطاق واسع، عبر اللغات.',
      items: [
        { icon: Globe, title: 'محور ذكاء متعدد اللغات', desc: 'ذكاء عربي وإنجليزي أصيل بسياق لغوي صحيح، وليس مجرد ترجمة.' },
        { icon: Zap, title: 'خط أنابيب نشر آلي', desc: 'من إشارة تحليلية إلى المحتوى المنشور — آلي تماماً، متعدد اللغات، متعدد القنوات.' },
        { icon: Bot, title: 'وكلاء ذكاء اصطناعي محادثيون', desc: 'وكلاء ذكيون مدرّبون على بيانات خاصة بالمجال للمحادثات على مستوى الأعمال.' },
        { icon: MessageCircle, title: 'بنية تحتية للقنوات', desc: 'تكاملات أصيلة مع تيليغرام وواتساب والتوزيع متعدد القنوات مستقبلاً.' },
        { icon: BarChart2, title: 'لوحة الذكاء', desc: 'مركز تحكم موحد لمراقبة خلاصات الذكاء وقوائم النشر وأداء الوكلاء.' },
        { icon: Shield, title: 'جاهز للعلامة التجارية', desc: 'هندسة معيارية مصممة للنشر بعلامة تجارية خاصة عبر الصناعات.' },
      ],
    },
    vision: {
      label: 'الرؤية الأشمل',
      h2: 'طبقة بنية تحتية لكل صناعة',
      sub: 'تبدأ كويدوري بالذكاء المالي، لكنها صُممت كبنية تحتية للجيل القادم من الأعمال الذكية متعددة اللغات.',
      industries: [
        { name: 'العيادات', icon: HeartPulse },
        { name: 'العقارات', icon: Building2 },
        { name: 'مكاتب المحاماة', icon: Scale },
        { name: 'المطاعم', icon: Utensils },
        { name: 'الفنادق', icon: Hotel },
        { name: 'المالية', icon: BarChart2 },
        { name: 'الأعمال المحلية', icon: Store },
      ],
      channels: {
        h: 'تواصل في كل مكان',
        sub: 'ستتمكن الأعمال من إطلاق وكلاء ذكاء اصطناعي بعلامتها التجارية على كل قناة رئيسية.',
        items: ['واتساب', 'تيليغرام', 'إنستغرام', 'تيك توك', 'يوتيوب', 'دردشة الويب'],
      },
    },
    cta: {
      h2: 'ابدأ البناء مع كويدوري',
      sub: 'انضم إلى الجيل القادم من الأعمال الذكية متعددة اللغات.',
      btn1: 'ابدأ مجاناً',
      btn2: 'احجز عرضاً',
    },
  },
  en: {
    hero: {
      badge: 'Arabic-First · Multilingual · AI-Powered',
      h1a: 'AI-Powered Multilingual',
      h1b: 'Intelligence & Publishing',
      h1c: 'Infrastructure',
      sub: 'Arabic-first AI systems built for the next generation of intelligent businesses.',
      cta1: 'Start Free',
      cta2: 'Book Demo',
    },
    products: {
      label: 'Our Ecosystem',
      h2: 'Four Intelligent Systems. One Platform.',
      sub: 'A complete AI intelligence and publishing infrastructure designed for multilingual digital businesses.',
      items: [
        { icon: Activity, color: 'indigo', tag: 'Intelligence', name: 'QuidPulse', desc: 'Real-time AI-curated crypto intelligence feed in Arabic and English. Signals, analysis, and market insights — delivered automatically.' },
        { icon: BrainCircuit, color: 'purple', tag: 'Analysis', name: 'Quidanalyzer', desc: 'Conversational AI assistant for deep crypto analysis, multilingual research, and on-demand market intelligence.' },
        { icon: Send, color: 'amber', tag: 'Publishing', name: 'Post Publisher', desc: 'AI-powered content creation and automated publishing system. Generate, schedule, and distribute multilingual content across channels.' },
        { icon: Video, color: 'emerald', tag: 'Video', name: 'Video Publisher', desc: 'Automated multilingual video creation, narration, and distribution pipeline powered by AI.' },
      ],
    },
    stats: [
      { value: '2+', label: 'Languages Supported' },
      { value: '4', label: 'Products' },
      { value: '10+', label: 'Industries Planned' },
      { value: '24/7', label: 'Automated Publishing' },
    ],
    features: {
      label: 'Platform Capabilities',
      h2: 'Built for Intelligence at Scale',
      sub: 'Every component of Quidory is designed to operate intelligently, at scale, across languages.',
      items: [
        { icon: Globe, title: 'Multilingual AI Core', desc: 'Native Arabic and English intelligence with proper linguistic context, not just translation.' },
        { icon: Zap, title: 'Automated Publishing Pipeline', desc: 'From intelligence signal to published content — fully automated, multilingual, multi-channel.' },
        { icon: Bot, title: 'Conversational AI Agents', desc: 'Intelligent agents trained on domain-specific data for business-grade conversations.' },
        { icon: MessageCircle, title: 'Channel Infrastructure', desc: 'Native integrations with Telegram, WhatsApp, and future multi-channel distribution.' },
        { icon: BarChart2, title: 'Intelligence Dashboard', desc: 'Unified control center for monitoring intelligence feeds, publishing queues, and agent performance.' },
        { icon: Shield, title: 'White-Label Ready', desc: 'Modular architecture designed for future white-label deployment across industries.' },
      ],
    },
    vision: {
      label: 'The Bigger Vision',
      h2: 'An AI Infrastructure Layer for Every Industry',
      sub: "Quidory begins with financial intelligence, but is engineered as the infrastructure layer for the next generation of multilingual intelligent businesses — across every industry, in every language.",
      industries: [
        { name: 'Clinics', icon: HeartPulse },
        { name: 'Real Estate', icon: Building2 },
        { name: 'Law Firms', icon: Scale },
        { name: 'Restaurants', icon: Utensils },
        { name: 'Hotels', icon: Hotel },
        { name: 'Finance', icon: BarChart2 },
        { name: 'Local Business', icon: Store },
      ],
      channels: {
        h: 'Connect Everywhere',
        sub: 'Businesses will launch branded AI agents connected to every major channel.',
        items: ['WhatsApp', 'Telegram', 'Instagram', 'TikTok', 'YouTube', 'Web Chat'],
      },
    },
    cta: {
      h2: 'Start Building with Quidory',
      sub: 'Join the next generation of multilingual intelligent businesses.',
      btn1: 'Start Free',
      btn2: 'Book Demo',
    },
  },
};

const colorMap = {
  indigo: { bg: 'bg-indigo-500/10 dark:bg-indigo-500/10', icon: 'text-indigo-500 dark:text-indigo-400', border: 'group-hover:border-indigo-500/30', badge: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400' },
  purple: { bg: 'bg-purple-500/10', icon: 'text-purple-500 dark:text-purple-400', border: 'group-hover:border-purple-500/30', badge: 'bg-purple-500/10 text-purple-600 dark:text-purple-400' },
  amber:  { bg: 'bg-amber-500/10',  icon: 'text-amber-500 dark:text-amber-400',  border: 'group-hover:border-amber-500/30',  badge: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  emerald:{ bg: 'bg-emerald-500/10',icon: 'text-emerald-500 dark:text-emerald-400', border: 'group-hover:border-emerald-500/30', badge: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
};

const LandingPage = () => {
  const { language, isRTL } = useLanguage();
  const { theme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const c = content[language];
  const isDark = mounted ? theme === 'dark' : true;

  return (
    <div className="w-full">
      <SEO
        titleEn="Quidory — Arabic-First AI Intelligence & Publishing Platform"
        titleAr="كويدوري — منصة الذكاء الاصطناعي والنشر الآلي عربية الجذور"
        descEn="AI-powered multilingual intelligence and publishing infrastructure. Arabic-first AI systems for the next generation of intelligent businesses."
        descAr="بنية تحتية ذكية متعددة اللغات للذكاء الاصطناعي والنشر الآلي. أنظمة ذكاء اصطناعي عربية الجذور للجيل القادم من الأعمال الذكية."
        path="/"
      />

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-16">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src={isDark ? HERO_DARK : HERO_LIGHT}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 ${isDark ? 'bg-[#050814]/75' : 'bg-white/70'}`} />
          {/* Radial glows */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_50%,rgba(99,102,241,0.08)_0%,transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_20%,rgba(168,85,247,0.06)_0%,transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_80%,rgba(212,175,55,0.04)_0%,transparent_60%)]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Text */}
            <div>
              {/* Badge */}
              <div className="animate-fade-in inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-gold/40 text-gold text-xs font-medium mb-8 tracking-wide">
                <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
                {c.hero.badge}
              </div>

              <h1 className={`animate-fade-in-up text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-foreground leading-tight mb-6 ${isRTL ? 'leading-snug' : ''}`}>
                {c.hero.h1a}{' '}
                <span className="gradient-text font-medium">{c.hero.h1b}</span>
                <br />
                <span className="text-gold font-medium">{c.hero.h1c}</span>
              </h1>

              <p className="animate-fade-in-up animate-delay-200 text-lg sm:text-xl text-muted-foreground max-w-lg mb-10 leading-relaxed">
                {c.hero.sub}
              </p>

              <div className="animate-fade-in-up animate-delay-300 flex flex-wrap gap-3">
                <LocaleLink
                  to="/quidanalyzer"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-foreground text-background font-medium text-sm hover:bg-foreground/90 transition-all duration-200 hover:-translate-y-0.5"
                >
                  {c.hero.cta1}
                  <ChevronRight className={`h-4 w-4 ${isRTL ? 'rtl-flip' : ''}`} />
                </LocaleLink>
                <LocaleLink
                  to="/contact"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-border text-foreground font-medium text-sm hover:bg-muted transition-all duration-200 hover:-translate-y-0.5"
                >
                  {c.hero.cta2}
                </LocaleLink>
              </div>
            </div>

            {/* Live animated dashboard demo */}
            <div className="animate-fade-in animate-delay-500 hidden md:block">
              <HeroDashboardDemo />
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce">
          <div className="h-6 w-px bg-muted-foreground/30 rounded-full" />
          <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
        </div>
      </section>

      {/* ── PRODUCTS ── */}
      <IntelligenceTicker />
      <section id="products" className="py-24 lg:py-32 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold uppercase tracking-widest text-gold mb-3 block">
              {c.products.label}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-foreground mb-4">
              {c.products.h2}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {c.products.sub}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {c.products.items.map((product, i) => {
              const colors = colorMap[product.color];
              const Icon = product.icon;
              return (
                <div
                  key={i}
                  data-testid={`product-card-${i}`}
                  className={`group relative p-6 rounded-xl border border-border bg-card hover:-translate-y-1 transition-all duration-300 cursor-pointer ${colors.border}`}
                >
                  <div className={`inline-flex p-2.5 rounded-lg ${colors.bg} mb-4`}>
                    <Icon className={`h-5 w-5 ${colors.icon}`} />
                  </div>
                  <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-md mb-3 ${colors.badge}`}>
                    {product.tag}
                  </span>
                  <h3 className="font-semibold text-foreground mb-2 text-start">{product.name}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed text-start">{product.desc}</p>
                  <div className={`mt-4 flex items-center gap-1 text-xs font-medium ${colors.icon} opacity-0 group-hover:opacity-100 transition-opacity duration-200`}>
                    <span>{language === 'ar' ? 'اعرف أكثر' : 'Learn more'}</span>
                    <ArrowRight className={`h-3 w-3 ${isRTL ? 'rtl-flip' : ''}`} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="py-14 border-y border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {c.stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl sm:text-4xl font-light text-foreground mb-1 tracking-tight">
                  <span className="text-gold font-medium">{stat.value}</span>
                </div>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="py-24 lg:py-32 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-gold mb-3 block">
                {c.features.label}
              </span>
              <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-foreground mb-4">
                {c.features.h2}
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                {c.features.sub}
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {c.features.items.map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <div key={i} data-testid={`feature-item-${i}`} className="group p-4 rounded-lg border border-border hover:border-border/80 hover:bg-card transition-all duration-200">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-md bg-gold/10 mt-0.5 shrink-0">
                          <Icon className="h-4 w-4 text-gold" />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-1">{f.title}</h4>
                          <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="relative hidden lg:block">
              <div className="absolute -inset-8 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 rounded-3xl" />
              <img
                src={AI_VIZ}
                alt="AI Intelligence Visualization"
                className="relative rounded-2xl border border-border/50 shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── VISION ── */}
      <section className="py-24 lg:py-32 bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold uppercase tracking-widest text-gold mb-3 block">
              {c.vision.label}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-foreground mb-4">
              {c.vision.h2}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {c.vision.sub}
            </p>
          </div>

          {/* Industries Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-16">
            {c.vision.industries.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <div
                  key={i}
                  data-testid={`industry-card-${i}`}
                  className="flex flex-col items-center gap-2.5 p-4 rounded-xl border border-border bg-background hover:border-gold/30 hover:bg-card transition-all duration-200 cursor-default group"
                >
                  <div className="p-2.5 rounded-lg bg-gold/10 group-hover:bg-gold/15 transition-colors">
                    <Icon className="h-5 w-5 text-gold" />
                  </div>
                  <span className="text-xs text-muted-foreground text-center font-medium leading-tight">{ind.name}</span>
                  <span className="text-[10px] text-gold/60 font-medium">{language === 'ar' ? 'قريباً' : 'Soon'}</span>
                </div>
              );
            })}
          </div>

          {/* Channels */}
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-xl font-medium text-foreground mb-2">{c.vision.channels.h}</h3>
            <p className="text-muted-foreground mb-6 text-sm">{c.vision.channels.sub}</p>
            <div className="flex flex-wrap justify-center gap-2">
              {c.vision.channels.items.map((ch, i) => (
                <span
                  key={i}
                  className="px-4 py-1.5 rounded-full border border-border bg-background text-sm text-muted-foreground hover:border-gold/30 hover:text-foreground transition-all duration-200"
                >
                  {ch}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 lg:py-32 bg-background relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.05)_0%,transparent_70%)]" />
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-foreground mb-4">
            {c.cta.h2}
          </h2>
          <p className="text-lg text-muted-foreground mb-10">
            {c.cta.sub}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <LocaleLink
              to="/quidanalyzer"
              data-testid="cta-start-free"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg bg-foreground text-background font-medium text-sm hover:bg-foreground/90 transition-all duration-200 hover:-translate-y-0.5"
            >
              {c.cta.btn1}
            </LocaleLink>
            <LocaleLink
              to="/contact"
              data-testid="cta-book-demo"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg border border-gold/40 text-gold font-medium text-sm hover:bg-gold/5 transition-all duration-200"
            >
              {c.cta.btn2}
            </LocaleLink>
          </div>
        </div>
      </section>

    </div>
  );
};

export default LandingPage;
